import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup
} from '@mui/material';
import { generateClient, GraphQLQuery } from 'aws-amplify/api';
import { useForm } from 'react-hook-form';
import { RoleType, UserUpdateMutation, UserUpdateMutationVariables } from '../../API';
import { userUpdate } from '../../graphql/mutations';
import { CloseIcon } from '../Icons/CommonIcons';
import { OrgUserType } from './OrgUserType.model';
import { useState } from 'react';

interface UpdateOrgUserProps {
  isOpen: boolean;
  currentOrgUser: OrgUserType;
  setCurrentOrgUser: (setCurrentOrgUser: OrgUserType) => void;
  currentOrgUsers: OrgUserType[];
  setCurrentOrgUsers: (users: OrgUserType[]) => void;
  setIsOpen: (isOpen: boolean) => void;
}

type UpdateUserFields = {
  role: string;
};

const UpdateOrgUserModal = ({
  setIsOpen,
  isOpen,
  currentOrgUser,
  setCurrentOrgUser,
  currentOrgUsers,
  setCurrentOrgUsers
}: UpdateOrgUserProps) => {
  const form = useForm<UpdateUserFields>();
  const { register, handleSubmit } = form;
  const [error, setError] = useState<string | undefined>();
  const client = generateClient();

  const onSubmit = async (data: UpdateUserFields) => {
    try {
      const result = await client.graphql<GraphQLQuery<UserUpdateMutation>>({
        query: userUpdate,
        variables: {
          id: currentOrgUser.id,
          input: { role: data.role as RoleType }
        } as UserUpdateMutationVariables
      });

      const errorMessage = result?.errors?.[0]?.message;
      if (errorMessage) {
        setError(errorMessage);
        return;
      }

      if (result.data?.userUpdate) {
        const updated = { ...currentOrgUser, role: data.role };
        setCurrentOrgUser(updated);
        setCurrentOrgUsers(
          currentOrgUsers.map((u) => (u.id === updated.id ? { ...u, role: updated.role } : u))
        );
        setIsOpen(false);
        setError(undefined);
      }
    } catch (err: any) {
      const message = err?.errors?.[0]?.message || err?.message || 'Unable to update user role';
      setError(message);
    }
  };

  return (
    <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth={'md'} fullWidth={true}>
      <DialogTitle>
        <div className="text-[24px] font-bold mb-[16px]">
          Edit User
          <IconButton
            aria-label="close"
            onClick={() => setIsOpen(false)}
            sx={{
              position: 'absolute',
              right: 28,
              top: 28
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className="scrollContainer">
        {currentOrgUser && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex justify-between mb-[36px]">
              <span className="w-[360px]">
                <DialogContentText className="text-black">Name</DialogContentText>
                <DialogContentText className="text-black text-[18px] mb-[32px]">
                  {currentOrgUser.username}
                </DialogContentText>
              </span>
              <span className="w-[360px]">
                <DialogContentText className="text-black">Email</DialogContentText>
                <DialogContentText className="text-black text-[18px] mb-[32px]">
                  {currentOrgUser.email}
                </DialogContentText>
              </span>
            </div>
            <FormControl className="mb-8">
              <FormLabel id="demo-row-radio-buttons-group-label" className="text-black">
                Role
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                defaultValue={currentOrgUser.role}
              >
                <FormControlLabel
                  value={RoleType.ADMIN}
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': { color: '#F58C29' },
                        '& .MuiSvgIcon-root': {
                          fontSize: 32
                        }
                      }}
                    />
                  }
                  label={RoleType.ADMIN}
                  {...register('role')}
                />
                <FormControlLabel
                  value={RoleType.HOST}
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': { color: '#F58C29' },
                        '& .MuiSvgIcon-root': {
                          fontSize: 32
                        }
                      }}
                    />
                  }
                  label={RoleType.HOST}
                  {...register('role')}
                />
              </RadioGroup>
            </FormControl>{' '}
            {error && (
              <DialogContentText className="text-red-600 mb-[16px]">{error}</DialogContentText>
            )}
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <button className="btn btn-neutral mr-[5px]" onClick={() => setIsOpen(false)}>
          Cancel
        </button>{' '}
        <button className="btn btn-primary" type="submit" onClick={handleSubmit(onSubmit)}>
          Save
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateOrgUserModal;
